import {
  GetItemPropsOptions,
  UseComboboxReturnValue
} from "downshift";
import debounce from "lodash.debounce";
import * as React from "react";
import styled, { css } from "styled-components";
import ReactDOM from "react-dom";

import { InputCss } from "src/library/components/Forms/Input";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import Icon from "src/library/components/Icon/Icon";
import type { IApiError } from "src/library/utils/errors/apiError";
import { Icons } from "src/library/components/Icon/types";
import {
  useEventListener,
  useOutsideItemClick
} from "src/library/hooks";
import { PopOutWrapper } from "src/library/components/ActionsPopOut";
import DropdownItem from "src/library/components/Dropdown/DropdownItem";

import { IUser } from "../api/users.api";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { detect } = require("detect-browser");

interface Props {
  // data = the string you'd like to search for
  onSearch: (data: string) => void;
  onClose: () => void;
  loading: boolean;
  searchResults: IUser[];
  placeholder: string;
  error: IApiError | null;
  downshiftProps: UseComboboxReturnValue<IUser>;
  autoFocus?: boolean;
}

export interface SearchRowProps extends GetItemPropsOptions<IUser> {
  user: IUser;
  disabled: boolean;
  highlighted: boolean;
}

interface ElementPosition {
  x: number;
  y: number;
}

const unsetPosition = {
  x: 0,
  y: 0
};

export const UserSearchComponent: React.ReactFCWithChildren<Props> = ({
  downshiftProps, onSearch, loading, searchResults, placeholder, error, onClose, autoFocus
}) => {
  const [ searchTerm, setSearchTerm ] = React.useState("");
  const browser = detect();
  const componentWrapperRef = React.useRef<HTMLDivElement>(null);
  const popoutRef = React.useRef<HTMLDivElement>(null);
  const portalWrapperRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [ position, setPosition ] = React.useState<ElementPosition>(unsetPosition);

  // when clicking outside component, run close callback
  useOutsideItemClick(popoutRef, () => onClose());

  const performSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    // hardcoded minimum no need for flexibilty yet
    const minimumChars = 1;

    if (e.target.value.length >= minimumChars) {
      onSearch(e.target.value);
      setSearchTerm(e.target.value);
    }
  }, 500);

  React.useEffect(() => {
    if (autoFocus) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [ autoFocus ]);

  // setOverlayPosition is used to determin where on the page we want this component to display.
  // It does this by grabbing the position of the element we want to position next to
  // and basing this components position on that.
  // The hard coded 400 and 350 are pixel values based on known hight and width values set in the component styles below.
  // Ran on mount or when element we are references changes position
  const setOverlayPosition = (autoScroll = false) => {
    if (portalWrapperRef.current) {
      const bounds = portalWrapperRef.current.getBoundingClientRect();

      setPosition({
        y: bounds.top + bounds.height,
        x: bounds.right - 400
      });

      if (autoScroll && browser.os !== "iOS") {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  };

  const handler = React.useCallback(() => {
    setOverlayPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ portalWrapperRef ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));

  React.useEffect(() => {
    // set position on mount
    setOverlayPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SearchComponent
      className="user-search-component"
      ref={componentWrapperRef}
    >
      <div
        className="search-wrapper"
      >
        <Icon
          className="search-icon"
          icon={Icons.SEARCH}
          $fillColor="lightGrey"
        />

        <input
          {...downshiftProps.getInputProps({
            placeholder: placeholder || "Search",
            className: "search-input",
            id: "search",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.persist();
              performSearch(e);
            },
            onFocus: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.persist();
              downshiftProps.openMenu();
            }
          }, { suppressRefError: true })}
          ref={inputRef}
        />

        {!loading && (
          <Icon
            className="close-search"
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={onClose}
          />
        )}
      </div>

      <div ref={portalWrapperRef}>
        {ReactDOM.createPortal(
          <StyledPopWrapper
            ref={popoutRef}
            className="pull-left push-top"
            {...position}
          >
            {searchTerm.length > 0 || searchResults.length > 0 ? (
              <ul
                className={`${downshiftProps.isOpen && "active"}`}
                {...downshiftProps.getMenuProps({}, { suppressRefError: true })}
              >
                <div className="header-wrapper">
                  <Text weight="bold">
                    {searchTerm.length ? `Showing results for "${searchTerm}"` : ""}
                  </Text>

                  {loading && (
                    <LoadingSpinner
                      type="flat"
                      position="inline"
                      size={10}
                    />
                  )}
                </div>

                {downshiftProps.isOpen && (
                  <>
                    {searchResults.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        {...downshiftProps.getItemProps({
                          item,
                          index
                        })}
                      >
                        <UserSearchDropdownItem highlighted={index === downshiftProps.highlightedIndex}>
                          <Text className="emphasize">
                            {`(${item.email})`}
                          </Text>

                          <Text className="details">
                            {`${item.firstName} ${item.lastName}`}
                          </Text>
                        </UserSearchDropdownItem>
                      </div>
                    ))}

                    {!searchResults.length && !error && !loading && (
                      <UserSearchDropdownItem>
                        <Text className="details">
                          Nothing found
                        </Text>
                      </UserSearchDropdownItem>
                    )}

                    {error && !loading && (
                      <UserSearchDropdownItem>
                        <Text className="error">
                          An unknown error occurred whilst performing your search, please try again.
                        </Text>
                      </UserSearchDropdownItem>
                    )}
                  </>
                )}
              </ul>
            ) : (
              <div className="pre-search">
                <Text className="details">
                  Start typing to find users
                </Text>
              </div>
            )}

          </StyledPopWrapper>
          , document.body)}
      </div>

      {loading && (
        <LoadingSpinner
          className="loading-spinner"
          type="circular"
          position="inline"
          size={20}
        />
      )}
    </SearchComponent>
  );
};

const StyledPopWrapper = styled(PopOutWrapper)`
  ${({ theme }) => css`
    margin: 0;
    width: 400px;

    .header-wrapper {
      display: flex;
      justify-content: space-between;
      padding: 0 ${theme.spacing.padding.md};
    }

    .pre-search {
      padding: 0 ${theme.spacing.padding.md};
      padding-bottom: ${theme.spacing.padding.md};
    }
  `}
`;

const UserSearchDropdownItem = styled(DropdownItem)`
  justify-content: space-between;
`;

const SearchComponent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex: 1;
    height: 40px;
    max-width: 400px;
    position: relative;

    [role="combobox"] {
      width: 100%;
    }

    .search-wrapper {
      position: relative;

      .search-icon {
        position: absolute;
        left: 5px;
        top: 50%;
        transform: translateY(-50%);
      }

      .close-search {
        position: absolute;
        right: 5px;
        top: 50%;
        transform: translateY(-50%);
        
        &:hover {
          cursor: pointer;
        }
      }

      .search-input {
        ${InputCss};
        width: 240px;
        max-width: 100%;
        border: solid 2px ${theme.colors.lightGrey};
        border-radius: ${theme.borderRadius};
        padding-left: 35px;
        transition: ${theme.transition.quickest};
        font-size: ${theme.fonts.sizes.p};

        &:hover,
        &:focus {
          border-color: ${theme.colors.primaryDark};
          background-color: ${theme.colors.white};
        }

        width: 100%;
        height: 100%;
        font-size: ${theme.fonts.sizes.p};

        &:focus {
          ::placeholder {
            color: ${theme.colors[ "primaryDark" ]};
          }
        }
      }

      &:hover {
        svg {
          fill: ${theme.colors.lightGrey};
        }
      }
    }

    .loading-spinner {
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
    }
  `};
`;
